import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Holiday {
    id: string;
    tenantId: string;
    name: string;
    date: string;
    type: string;
    description?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface HolidayState {
    holidays: Holiday[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchHolidays: (filters?: any) => Promise<void>;
    createHoliday: (data: Partial<Holiday>) => Promise<void>;
    updateHoliday: (id: string, data: Partial<Holiday>) => Promise<void>;
    deleteHoliday: (id: string) => Promise<void>;
}

export const useHolidayStore = create<HolidayState>((set, get) => ({
    holidays: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 100,
        total: 0,
        totalPages: 0,
    },
    fetchHolidays: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/holidays', { params: { limit: 100, ...filters } });
            const { data, pagination } = response.data;
            set({ holidays: data || [], pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch holidays', isLoading: false });
        }
    },
    createHoliday: async (data: Partial<Holiday>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/people/holidays', data);
            const newHoliday = response.data?.data ?? response.data;
            set((state) => ({
                holidays: [...state.holidays, newHoliday].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create holiday', isLoading: false });
            throw error;
        }
    },
    updateHoliday: async (id: string, data: Partial<Holiday>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/people/holidays/${id}`, data);
            const updatedHoliday = response.data?.data ?? response.data;
            set((state) => ({
                holidays: state.holidays.map((o) => (o.id === id ? updatedHoliday : o)).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update holiday', isLoading: false });
            throw error;
        }
    },
    deleteHoliday: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
            await apiClient.delete(`/people/holidays/${id}`);
            set((state) => ({
                holidays: state.holidays.filter((o) => o.id !== id),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete holiday', isLoading: false });
            throw error;
        }
    },
}));
